import { deburr, defaults, template as templateLodash } from 'lodash-es';

import { Platform } from '@/constants/platform.constant';

/**
 * It takes a string with placeholders and replaces them with values from an object.
 * @param {string} templateString - The string that contains the template.
 * @param {object} data - The data object that will be used to replace the template variables.
 * @returns A string with the data interpolated into the template.
 */
export const parseTemplate = (
  templateString: string,
  data: object,
  regexp = /:([a-zA-Z0-9]*)/g
) => {
  const compiled = templateLodash(templateString, {
    interpolate: regexp
  });

  return compiled(data);
};

/**
 * It takes a template string and a string, and returns an object with the values of the template
 * string's placeholders.
 * @param {string} templateString - The string that contains the template.
 * @param {string} string - The string to parse
 * @returns An object with the values of the placeholders.
 */
export const parseString = (
  templateString: string,
  string: string,
  regexp = /:([a-zA-Z0-9]*)/g
) => {
  const templateRegex = templateString?.replace(regexp, '(?<$1>.*)') || '';

  const { groups = null } = string?.match(templateRegex) || {};

  return groups;
};

/**
 * It takes a string, replaces any number of spaces with a single space, and then trims the string
 * @param {string} value - string - The string to trim.
 */
export const trimString = (value: string) =>
  value?.replace(/ {2,}/g, ' ')?.trim() || '';

/**
 * It takes a string, replaces all whitespace with a single space, and trims the string
 * @param {string} value - The string to trim.
 */
export const trimStringToSingleLine = (value: string) =>
  value?.replace(/ {2,}|\t|\n|\r/g, ' ')?.trim() || '';

/**
 * The function checks if a given value is a string representation of a number.
 * @param {string} value - The `value` parameter is a string that we want to check if it represents
 * a valid number.
 * @returns a boolean value.
 */
export const isStringNumber = (value: string): boolean => {
  if (typeof value !== 'string') {
    return false;
  }

  return !Number.isNaN(Number(value));
};

/**
 * The function takes a string as input and returns a number if the string can be
 * parsed as a number, otherwise it returns null.
 * @param {string} value - The `value` parameter is a string that you want to parse into a number.
 * @returns The function `parseStringToNumber` returns a number if the input string can be parsed
 * into a number, otherwise it returns `null`.
 */
export const parseStringToNumber = (value: string): number | null => {
  if (!isStringNumber(value)) {
    return null;
  }

  return Number(value);
};

/**
 * It takes a file and returns a promise that resolves to the base64 encoded string of the file
 * @param {File} file - The file to be converted to base64
 * @returns A promise that resolves to a string.
 */
export const getBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

/**
 * It takes a base64 string and returns the content of the base64 string
 * @param {string} base64 - The base64 string that you want to get the content from.
 * @returns The content of the base64 string.
 */
export const getContentFromBase64 = (base64: string) =>
  base64.substring(base64.indexOf(',') + 1);

/**
 * It takes a base64 string and returns the mime type
 * @param {string} base64 - The base64 string that you want to convert to a file.
 * @returns The mime type of the file.
 */
export const getMimeTypeFromBase64 = (base64: string) => {
  const mimeType = base64.match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/);
  if (mimeType) {
    return mimeType[0];
  }
  return null;
};

/**
 * The function cleans up a filename by replacing non-word characters (excluding the last period)
 * with underscores and removing diacritics.
 */
export const cleanupFileName = (filename: string) =>
  filename ? deburr(filename).replace(/[^\w]+(?=.*[\.])/g, '_') : '';

/**
 * Is app prozis store
 */
export const isAppProzisStore = (): boolean => {
  const userAgent = navigator?.userAgent?.toLowerCase() || '';

  return userAgent.includes('appprozisstore');
};

/**
 * Is app prozis store ios.
 */
export const isAppProzisStoreIOS = (): boolean => {
  const userAgent = navigator?.userAgent?.toLowerCase() || '';

  return isAppProzisStore() && userAgent.includes('ios');
};

/**
 * Is app prozis store android.
 */
export const isAppProzisStoreAndroid = (): boolean => {
  const userAgent = navigator?.userAgent?.toLowerCase() || '';

  return isAppProzisStore() && userAgent.includes('android');
};

/**
 * Open url.
 * Default target: '_blank'
 * Default features: 'noopener noreferrer'
 * `forceExternal`: inside Prozis store, opens `www.prozis.com` urls on the
 * external browser instead of the internal store.
 */
export const openURL = (options: {
  features?: string;
  forceExternal?: boolean;
  target?: '_blank' | '_self';
  url: string;
}) => {
  try {
    const { features, forceExternal, target, url } = defaults(options, {
      features: 'noopener noreferrer',
      forceExternal: false,
      target: '_blank'
    });

    if (url) {
      // Inside Prozis store.
      if (isAppProzisStore()) {
        // Prozis url.
        const regexProzis = /\bwww\.prozis\.com\b/;
        const isProzisUrl = !forceExternal && regexProzis.test(url);

        // Partnership url.
        const regexPartnership = /\bpartnership\.prozis\.com\b/;
        const isPartnershipUrl =
          regexPartnership.test(url) || url.startsWith('/');

        // The url is from `www.prozis.com`.
        if (isProzisUrl) {
          const data = JSON.stringify({
            openMode: 'INTERNAL-STORE',
            type: 'URL',
            url
          });

          if (isAppProzisStoreIOS()) {
            window.webkit.messageHandlers.navigation.postMessage(data);
          } else {
            window.native.navigation(data);
          }
        }
        // The url is from `partnership.prozis.com`.
        else if (isPartnershipUrl) {
          window.open(url, '_self', features);
        }
        // From others url's.
        else {
          const data = JSON.stringify({
            openMode: 'EXTERNAL',
            type: 'URL',
            url
          });

          if (isAppProzisStoreIOS()) {
            window.webkit.messageHandlers.navigation.postMessage(data);
          } else {
            window.native.navigation(data);
          }
        }
      }
      // Outside Prozis store.
      else {
        window.open(url, target, features);
      }
    } else {
      console.error('Empty url');
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * Download asset from url.
 */
export const downloadAssetFromUrl = (url: string, name: string) => {
  try {
    if (isAppProzisStore() && process.env.BUILD_MODE === 'prd') {
      const data = JSON.stringify({
        openMode: 'EXTERNAL',
        type: 'URL',
        url
      });

      if (isAppProzisStoreIOS()) {
        window.webkit.messageHandlers.navigation.postMessage(data);
      } else {
        window.native.navigation(data);
      }
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * Get current platform.
 */
export const getCurrentPlatform = () => {
  let currentPlatform: Platform = Platform.DESKTOP;

  const isAppleDevice = /ipad|iphone|ipod/.test(
    navigator.userAgent.toLowerCase()
  );
  const isAndroidDevice = /android/.test(navigator.userAgent.toLowerCase());

  if (isAppProzisStore()) {
    currentPlatform = Platform.APP_PROZIS_STORE;
  } else if (isAppleDevice || isAndroidDevice) {
    currentPlatform = Platform.MOBILE;
  }

  return currentPlatform;
};
